// src/pages/Admin/Users/AdminUserDetails.jsx
import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Table, Form, Modal, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft, FaEdit, FaCheck, FaUser, FaEnvelope, FaPhone,
  FaBuilding, FaIdCard, FaMapMarkerAlt, FaExchangeAlt,
  FaFileAlt, FaHeadset, FaEye, FaStore
} from 'react-icons/fa';
import Sidebar from '../../../components/Common/Sidebar';
import { adminAPI, adminMerchantsAPI, adminEDCAPI } from '../../../services/api';
import { toast } from 'react-toastify';

// ── shared mini-components ────────────────────────────────────────────────────
const C = {
  border: '#e8e8f1', ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', danger: '#EF4444', radius: 10,
};

const inputStyle = {
  width: '100%', padding: '8px 11px', fontSize: 13, color: C.ink,
  background: '#f9f9fc', border: `1px solid ${C.border}`, borderRadius: 7,
  outline: 'none', boxSizing: 'border-box',
};

const labelStyle = { fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 4, display: 'block' };

const Btn = ({ onClick, color = C.primary, children, disabled, style = {} }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: color, color: '#fff', border: 'none', borderRadius: 7,
      padding: '7px 16px', fontSize: 13, fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, ...style,
    }}
  >{children}</button>
);

const SectionDivider = ({ title }) => (
  <div className="mt-4 pt-4" style={{ borderTop: '1px solid #f0f0f0' }}>
    <h6 className="fw-bold mb-3" style={{ color: '#14142b', borderBottom: '1px solid #e8e8f1', paddingBottom: '10px' }}>
      {title}
    </h6>
  </div>
);
// ─────────────────────────────────────────────────────────────────────────────

const AdminUserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusRemarks, setStatusRemarks] = useState('');
  const [updating, setUpdating] = useState(false);

  const [settlementCycle, setSettlementCycle] = useState('t3');
  const [settlementPct,   setSettlementPct]   = useState('3.00');
  const [savingSettlement, setSavingSettlement] = useState(false);

  // Bank details state
  const [bankForm, setBankForm] = useState({
    bank_holder_name: '', bank_account_no: '', bank_ifsc: '',
    bank_name: '', bank_branch: '', account_type: 'savings',
  });
  const [bankEditing, setBankEditing] = useState(false);
  const [bankSaving, setBankSaving]   = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState(false);

  // Transaction limit state
  const [txnLimit, setTxnLimit]           = useState(6000);
  const [txnLimitSaving, setTxnLimitSaving] = useState(false);

  // Partner Gateway IDs
  const [partnerIds, setPartnerIds] = useState({ cashfree_merchant_id: '', phonepe_merchant_id: '' });
  const [partnerIdsSaving, setPartnerIdsSaving] = useState(false);

  // POS Terminal
  const [posTerminal, setPosTerminal]       = useState(null);
  const [posForm, setPosForm]               = useState({ store_id: '', store_name: '', terminal_id: '', provider_id: '' });
  const [posEditing, setPosEditing]         = useState(false);
  const [posSaving, setPosSaving]           = useState(false);
  const [posLoading, setPosLoading]         = useState(false);

  useEffect(() => { fetchUserDetails(); }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.users.getDetails(userId);
      const data = response.data.data;
      setUserData(data);
      setNewStatus(data.user.status);
      setSettlementCycle(data.user.settlement_cycle || 't3');
      setSettlementPct(data.user.settlement_percentage || '3.00');

      // Bank details from user object
      const u = data.user;
      const hasBank = !!(u.bank_account_no || u.bank_holder_name || u.bank_ifsc);
      setHasBankDetails(hasBank);
      setBankForm({
        bank_holder_name: u.bank_holder_name || '',
        bank_account_no:  u.bank_account_no  || '',
        bank_ifsc:        u.bank_ifsc         || '',
        bank_name:        u.bank_name         || '',
        bank_branch:      u.bank_branch       || '',
        account_type:     u.account_type      || 'savings',
      });
      setTxnLimit(u.transaction_limit ?? 6000);
      setPartnerIds({
        cashfree_merchant_id: u.cashfree_merchant_id || '',
        phonepe_merchant_id:  u.phonepe_merchant_id  || '',
      });

      // Load POS terminal if pos_merchant
      if (u.user_role === 'pos_merchant') {
        setPosLoading(true);
        adminEDCAPI.listTerminals({ merchant_id: u.id, per_page: 100 })
          .then(r => {
            const list = r.data?.data?.data || r.data?.data || r.data?.terminals || [];
            const t = Array.isArray(list) ? list[0] || null : null;
            setPosTerminal(t);
            if (t) setPosForm({ store_id: t.store_id || '', store_name: t.store_name || '', terminal_id: t.terminal_id || '', provider_id: t.provider_id || '' });
          })
          .catch(() => {})
          .finally(() => setPosLoading(false));
      }
    } catch (error) {
      toast.error('Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      setUpdating(true);
      await adminAPI.users.updateStatus(userId, { status: newStatus, remarks: statusRemarks });
      toast.success('Status updated successfully');
      setShowStatusModal(false);
      fetchUserDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleSettlementUpdate = async () => {
    try {
      setSavingSettlement(true);
      await adminAPI.users.updateSettlementConfig(userId, {
        settlement_cycle:      settlementCycle,
        settlement_percentage: parseFloat(settlementPct),
      });
      toast.success('Settlement config updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update settlement config');
    } finally {
      setSavingSettlement(false);
    }
  };

  const saveBankDetails = async () => {
    setBankSaving(true);
    try {
      await adminMerchantsAPI.updateBankDetails(userId, bankForm);
      toast.success('Bank details saved');
      setBankEditing(false);
      setHasBankDetails(true);
    } catch {
      toast.error('Failed to save bank details');
    } finally { setBankSaving(false); }
  };

  const deleteBankDetails = async () => {
    if (!window.confirm('Remove all bank details for this user?')) return;
    try {
      await adminMerchantsAPI.deleteBankDetails(userId);
      toast.success('Bank details removed');
      setBankForm({ bank_holder_name: '', bank_account_no: '', bank_ifsc: '', bank_name: '', bank_branch: '', account_type: 'savings' });
      setHasBankDetails(false);
      setBankEditing(false);
    } catch {
      toast.error('Failed to remove bank details');
    }
  };

  const savePosTerminal = async () => {
    setPosSaving(true);
    try {
      if (posTerminal) {
        await adminEDCAPI.updateTerminal(posTerminal.id, { ...posForm, merchant_id: user.id });
        toast.success('POS Terminal updated');
      } else {
        const r = await adminEDCAPI.assignTerminal({ ...posForm, merchant_id: user.id });
        setPosTerminal(r.data?.data || r.data?.terminal || posForm);
        toast.success('POS Terminal assigned');
      }
      setPosEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save terminal');
    } finally { setPosSaving(false); }
  };

  const savePartnerIds = async () => {
    setPartnerIdsSaving(true);
    try {
      await adminMerchantsAPI.updatePartnerIds(userId, partnerIds);
      toast.success('Partner Gateway IDs saved');
    } catch {
      toast.error('Failed to save partner IDs');
    } finally { setPartnerIdsSaving(false); }
  };

  const saveTxnLimit = async () => {
    setTxnLimitSaving(true);
    try {
      await adminMerchantsAPI.updateTxnLimit(userId, txnLimit);
      toast.success('Transaction limit updated');
    } catch {
      toast.error('Failed to update limit');
    } finally { setTxnLimitSaving(false); }
  };

  const getStatusColor = (status) => {
    const map = { approved: '#4caf50', pending: '#ff9800', rejected: '#ff4d4f', suspended: '#666', not_submitted: '#999' };
    return map[status] || '#999';
  };

  const getStatusBg = (status) => {
    const map = { approved: '#f1f8e9', pending: '#fff8e1', rejected: '#fff1f0', suspended: '#f5f5f5', not_submitted: '#f5f5f5' };
    return map[status] || '#f5f5f5';
  };

  const StatusBadge = ({ status }) => (
    <span style={{
      padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600,
      color: getStatusColor(status), backgroundColor: getStatusBg(status),
      border: `1px solid ${getStatusColor(status)}33`, textTransform: 'capitalize',
      whiteSpace: 'nowrap',
    }}>
      {status?.replace(/_/g, ' ') || '—'}
    </span>
  );

  const tabs = [
    { key: 'details',      label: 'Details',      icon: FaUser },
    { key: 'transactions', label: 'Transactions', icon: FaExchangeAlt },
    { key: 'tickets',      label: 'Support',      icon: FaHeadset },
  ];

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar role="admin" />
        <div className="dashboard-content d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner animation="border" style={{ color: '#7c3aed' }} />
            <p className="mt-3 text-muted">Loading user details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="dashboard-layout">
        <Sidebar role="admin" />
        <div className="dashboard-content text-center py-5">
          <h5>User not found</h5>
          <Button variant="primary" onClick={() => navigate('/admin/users')}>Back to Users</Button>
        </div>
      </div>
    );
  }

  const { user, statistics } = userData;

  return (
    <div className="dashboard-layout">
      <Sidebar role="admin" />
      <div className="dashboard-content">

        {/* Back + Header */}
        <div className="mb-4">
          <Button variant="outline-secondary" size="sm" className="mb-3" onClick={() => navigate('/admin/users')}>
            <FaArrowLeft className="me-2" />Back to Users
          </Button>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h2 className="fw-bold mb-1">{user.username}</h2>
              <small className="text-muted">#{user.id} · {user.mid}</small>
            </div>
            <Button variant="primary" size="sm" onClick={() => setShowStatusModal(true)}>
              <FaEdit className="me-2" />Update Status
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="row g-3 mb-4">
          {[
            { label: 'Account Balance',   value: `₹${parseFloat(user.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,       color: '#7c3aed', bg: '#f1f8e9' },
            { label: 'Total Deposits',    value: `₹${statistics?.total_deposits || '0.00'}`,    color: '#4caf50', bg: '#e8f5e9' },
            { label: 'Total Withdrawals', value: `₹${statistics?.total_withdrawals || '0.00'}`, color: '#ff4d4f', bg: '#fff1f0' },
            { label: 'Total Commissions', value: `₹${statistics?.total_commissions || '0.00'}`, color: '#ff9800', bg: '#fff8e1' },
          ].map((s, i) => (
            <div className="col-6 col-md-3" key={i}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${s.color}` }}>
                <Card.Body className="py-3">
                  <p className="text-muted mb-1" style={{ fontSize: '0.78rem' }}>{s.label}</p>
                  <h5 className="fw-bold mb-0" style={{ color: s.color }}>{s.value}</h5>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Card className="border-0 shadow-sm">

          {/* Tab Nav */}
          <div style={{ borderBottom: '1px solid #e8e8e8', padding: '0 20px', display: 'flex', gap: '4px', overflowX: 'auto' }}>
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  border: 'none', background: 'none', padding: '14px 18px',
                  fontSize: '0.875rem', fontWeight: activeTab === t.key ? 600 : 400,
                  color: activeTab === t.key ? '#7c3aed' : '#666',
                  background: activeTab === t.key ? '#f1ebff' : 'transparent', color: activeTab === t.key ? '#7c3aed' : '#6e6e86', borderRadius: 8, border: 'none',
                  cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px',
                  transition: 'all 0.2s',
                }}
              >
                <t.icon size={13} />
                {t.label}
              </button>
            ))}
          </div>

          <Card.Body style={{ padding: '24px' }}>

            {/* ── DETAILS TAB ── */}
            {activeTab === 'details' && (
              <div className="row g-4">

                {/* Account Info */}
                <div className="col-md-6">
                  <h6 className="fw-bold mb-3" style={{ color: '#14142b', borderBottom: '1px solid #e8e8f1', paddingBottom: '10px' }}>
                    Account Information
                  </h6>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { icon: FaUser,      label: 'Username',   value: user.username },
                      { icon: FaEnvelope,  label: 'Email',      value: user.email },
                      { icon: FaPhone,     label: 'Phone',      value: user.phone || '—' },
                      { icon: FaBuilding,  label: 'Role',       value: user.user_role },
                      { icon: FaIdCard,    label: 'MID',        value: user.mid },
                    ].map((row, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: '#f1f8e9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <row.icon size={13} color="#7c3aed" />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#999', lineHeight: 1 }}>{row.label}</div>
                          <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#14142b', marginTop: '2px' }}>{row.value}</div>
                        </div>
                      </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: '#f1f8e9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FaCheck size={13} color="#7c3aed" />
                      </div>
                      <div>
                        <div style={{ fontSize: '0.72rem', color: '#999', lineHeight: 1 }}>Status</div>
                        <div style={{ marginTop: '4px' }}>
                          <StatusBadge status={user.status} />
                          <span style={{ marginLeft: '6px' }}>
                            <StatusBadge status={user.account_status} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Profile Info */}
                <div className="col-md-6">
                  <h6 className="fw-bold mb-3" style={{ color: '#14142b', borderBottom: '1px solid #e8e8f1', paddingBottom: '10px' }}>
                    Business Profile
                  </h6>
                  {user.profile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {[
                        { icon: FaUser,        label: 'Full Name',      value: user.profile.full_name },
                        { icon: FaBuilding,    label: 'Business Name',  value: user.profile.business_name },
                        { icon: FaMapMarkerAlt,label: 'Address',        value: user.profile.address },
                        { icon: FaMapMarkerAlt,label: 'City / State',   value: `${user.profile.city || '—'}, ${user.profile.state || '—'}` },
                        { icon: FaMapMarkerAlt,label: 'Pincode',        value: user.profile.pincode },
                        { icon: FaFileAlt,     label: 'PAN',            value: user.profile.pan_number || '—' },
                        { icon: FaFileAlt,     label: 'GST',            value: user.profile.gst_number || '—' },
                        { icon: FaIdCard,      label: 'Aadhaar',        value: user.profile.aadhaar_number || '—' },
                      ].map((row, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <row.icon size={13} color="#999" />
                          </div>
                          <div>
                            <div style={{ fontSize: '0.72rem', color: '#999', lineHeight: 1 }}>{row.label}</div>
                            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#14142b', marginTop: '2px' }}>{row.value || '—'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '32px', color: '#999', backgroundColor: '#f9f9f9', borderRadius: '12px' }}>
                      <FaUser size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
                      <p className="mb-0" style={{ fontSize: '0.875rem' }}>No profile information available</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── SETTLEMENT CONFIG ── */}
            {activeTab === 'details' && (
              <div className="mt-4 pt-4" style={{ borderTop: '1px solid #f0f0f0' }}>
                <h6 className="fw-bold mb-3" style={{ color: '#14142b', borderBottom: '1px solid #e8e8f1', paddingBottom: '10px' }}>
                  Settlement Configuration
                </h6>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      Settlement Cycle
                    </label>
                    <Form.Select
                      value={settlementCycle}
                      onChange={e => setSettlementCycle(e.target.value)}
                      style={{ minWidth: 140, fontSize: '0.875rem' }}
                    >
                      <option value="t1">T+1</option>
                      <option value="t2">T+2</option>
                      <option value="t3">T+3</option>
                    </Form.Select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      Settlement %
                    </label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={settlementPct}
                      onChange={e => setSettlementPct(e.target.value)}
                      style={{ width: 120, fontSize: '0.875rem' }}
                    />
                  </div>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleSettlementUpdate}
                    disabled={savingSettlement}
                    style={{ height: 38, padding: '0 20px' }}
                  >
                    {savingSettlement ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                    Save
                  </Button>
                </div>
              </div>
            )}

            {/* ── BANK DETAILS ── */}
            {activeTab === 'details' && (
              <div className="mt-4 pt-4" style={{ borderTop: '1px solid #f0f0f0' }}>
                <h6 className="fw-bold mb-3" style={{ color: '#14142b', borderBottom: '1px solid #e8e8f1', paddingBottom: '10px' }}>
                  Bank Details
                </h6>

                {!bankEditing ? (
                  <>
                    {hasBankDetails ? (
                      <div className="row g-3 mb-3">
                        {[
                          { label: 'Account Holder', value: bankForm.bank_holder_name },
                          { label: 'Account Number', value: bankForm.bank_account_no },
                          { label: 'IFSC Code',      value: bankForm.bank_ifsc },
                          { label: 'Bank Name',      value: bankForm.bank_name },
                          { label: 'Branch',         value: bankForm.bank_branch },
                          { label: 'Account Type',   value: bankForm.account_type },
                        ].map(({ label, value }) => (
                          <div className="col-6 col-md-4" key={label}>
                            <div style={{ fontSize: '0.72rem', color: '#999', marginBottom: 2 }}>{label}</div>
                            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#14142b' }}>{value || '—'}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: C.muted, fontSize: 13, marginBottom: 12 }}>No bank details added yet.</p>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Btn onClick={() => setBankEditing(true)}>
                        {hasBankDetails ? 'Edit Details' : 'Add Bank Details'}
                      </Btn>
                      {hasBankDetails && (
                        <Btn onClick={deleteBankDetails} color={C.danger}>Remove</Btn>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="row g-3">
                    {[
                      { key: 'bank_holder_name', label: 'Account Holder Name *' },
                      { key: 'bank_account_no',  label: 'Account Number *' },
                      { key: 'bank_ifsc',        label: 'IFSC Code *' },
                      { key: 'bank_name',        label: 'Bank Name' },
                      { key: 'bank_branch',      label: 'Branch' },
                    ].map(({ key, label }) => (
                      <div className="col-6" key={key}>
                        <label style={labelStyle}>{label}</label>
                        <input
                          style={inputStyle}
                          value={bankForm[key]}
                          onChange={e => setBankForm(p => ({ ...p, [key]: e.target.value }))}
                          placeholder={label.replace(' *', '')}
                        />
                      </div>
                    ))}
                    <div className="col-6">
                      <label style={labelStyle}>Account Type</label>
                      <select
                        style={inputStyle}
                        value={bankForm.account_type}
                        onChange={e => setBankForm(p => ({ ...p, account_type: e.target.value }))}
                      >
                        <option value="savings">Savings</option>
                        <option value="current">Current</option>
                      </select>
                    </div>
                    <div className="col-12" style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <Btn onClick={saveBankDetails} disabled={bankSaving}>
                        {bankSaving ? 'Saving…' : 'Save Details'}
                      </Btn>
                      <Btn onClick={() => setBankEditing(false)} color={C.muted}>Cancel</Btn>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── TRANSACTION LIMIT (admin only) ── */}
            {activeTab === 'details' && (
              <div className="mt-4 pt-4" style={{ borderTop: '1px solid #f0f0f0' }}>
                <h6 className="fw-bold mb-1" style={{ color: '#14142b', borderBottom: '1px solid #e8e8f1', paddingBottom: '10px' }}>
                  Transaction Limit <span style={{ fontSize: 11, fontWeight: 400, color: C.muted, marginLeft: 6 }}>(Admin Only — not visible to merchant)</span>
                </h6>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
                  Maximum amount per single payment. Default: ₹6,000.
                </p>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div>
                    <label style={labelStyle}>Limit (₹)</label>
                    <input
                      type="number"
                      min="1"
                      style={{ ...inputStyle, width: 160 }}
                      value={txnLimit}
                      onChange={e => setTxnLimit(e.target.value)}
                    />
                  </div>
                  <Btn onClick={saveTxnLimit} disabled={txnLimitSaving}>
                    {txnLimitSaving ? 'Saving…' : 'Save Limit'}
                  </Btn>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: C.body }}>
                  Current limit: <strong>₹{Number(txnLimit).toLocaleString('en-IN')}</strong> per transaction
                </div>
              </div>
            )}

            {/* ── PARTNER GATEWAY IDs ── */}
            {activeTab === 'details' && (
              <div className="mt-4 pt-4" style={{ borderTop: '1px solid #f0f0f0' }}>
                <h6 className="fw-bold mb-1" style={{ color: '#14142b', borderBottom: '1px solid #e8e8f1', paddingBottom: '10px' }}>
                  Partner Gateway IDs <span style={{ fontSize: 11, fontWeight: 400, color: C.muted, marginLeft: 6 }}>(Admin Only — Cashfree &amp; PhonePe sub-merchant MIDs)</span>
                </h6>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
                  Set the merchant's MID on Cashfree/PhonePe partner portal. Used as X-MERCHANT-ID when processing payments.
                </p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label style={labelStyle}>Cashfree Merchant ID</label>
                    <input
                      style={inputStyle}
                      value={partnerIds.cashfree_merchant_id}
                      onChange={e => setPartnerIds(p => ({ ...p, cashfree_merchant_id: e.target.value }))}
                      placeholder="e.g. CF_MERCHANT_12345"
                    />
                  </div>
                  <div className="col-md-6">
                    <label style={labelStyle}>PhonePe Merchant ID (MID)</label>
                    <input
                      style={inputStyle}
                      value={partnerIds.phonepe_merchant_id}
                      onChange={e => setPartnerIds(p => ({ ...p, phonepe_merchant_id: e.target.value }))}
                      placeholder="e.g. TSPA1XPAYUAT"
                    />
                  </div>
                  <div className="col-12">
                    <Btn onClick={savePartnerIds} disabled={partnerIdsSaving}>
                      {partnerIdsSaving ? 'Saving…' : 'Save Partner IDs'}
                    </Btn>
                  </div>
                </div>
              </div>
            )}

            {/* ── POS TERMINAL (pos_merchant only) ── */}
            {activeTab === 'details' && user.user_role === 'pos_merchant' && (
              <div className="mt-4 pt-4" style={{ borderTop: '1px solid #f0f0f0' }}>
                <h6 className="fw-bold mb-1" style={{ color: '#14142b', borderBottom: '1px solid #e8e8f1', paddingBottom: '10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaStore size={14} color="#7c3aed" /> POS Terminal Assignment
                  <span style={{ fontSize: 11, fontWeight: 400, color: C.muted, marginLeft: 4 }}>(Admin Only)</span>
                </h6>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
                  Assign a PhonePe EDC terminal to this POS merchant. Store ID and Terminal ID are provided by PhonePe.
                </p>

                {posLoading ? (
                  <p style={{ fontSize: 13, color: C.muted }}>Loading terminal…</p>
                ) : !posEditing ? (
                  <>
                    {posTerminal ? (
                      <div className="row g-3 mb-3">
                        {[
                          { label: 'Store ID',     value: posTerminal.store_id },
                          { label: 'Store Name',   value: posTerminal.store_name },
                          { label: 'Terminal ID',  value: posTerminal.terminal_id },
                          { label: 'Provider ID',  value: posTerminal.provider_id },
                          { label: 'Status',       value: posTerminal.status },
                        ].map(({ label, value }) => (
                          <div className="col-6 col-md-4" key={label}>
                            <div style={{ fontSize: '0.72rem', color: '#999', marginBottom: 2 }}>{label}</div>
                            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#14142b' }}>{value || '—'}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#92400E', marginBottom: 12 }}>
                        No terminal assigned yet. Click below to assign one.
                      </div>
                    )}
                    <Btn onClick={() => setPosEditing(true)}>
                      {posTerminal ? 'Edit Terminal' : 'Assign Terminal'}
                    </Btn>
                  </>
                ) : (
                  <div className="row g-3">
                    {[
                      { key: 'store_id',    label: 'Store ID *',    placeholder: 'e.g. SB2209091159534485765558' },
                      { key: 'store_name',  label: 'Store Name *',  placeholder: 'e.g. My Store Mumbai' },
                      { key: 'terminal_id', label: 'Terminal ID *', placeholder: 'e.g. T2209091159534485765559' },
                      { key: 'provider_id', label: 'Provider ID',   placeholder: 'e.g. A1XPAYPID (leave blank for default)' },
                    ].map(({ key, label, placeholder }) => (
                      <div className="col-md-6" key={key}>
                        <label style={labelStyle}>{label}</label>
                        <input
                          style={inputStyle}
                          value={posForm[key]}
                          onChange={e => setPosForm(p => ({ ...p, [key]: e.target.value }))}
                          placeholder={placeholder}
                        />
                      </div>
                    ))}
                    <div className="col-12" style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <Btn onClick={savePosTerminal} disabled={posSaving}>
                        {posSaving ? 'Saving…' : posTerminal ? 'Update Terminal' : 'Assign Terminal'}
                      </Btn>
                      <Btn onClick={() => setPosEditing(false)} color={C.muted}>Cancel</Btn>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── TRANSACTIONS TAB ── */}
            {activeTab === 'transactions' && (
              <div>
                <h6 className="fw-bold mb-3">
                  Transactions
                  <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#999', fontWeight: 400 }}>
                    ({statistics?.transaction_count || 0} total)
                  </span>
                </h6>
                {user.transactions && user.transactions.length > 0 ? (
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Transaction ID</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Gateway</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {user.transactions.map((txn) => (
                        <tr key={txn.id}>
                          <td><small className="font-monospace text-muted">{txn.transaction_id}</small></td>
                          <td>
                            <Badge bg={txn.transaction_type === 'deposit' ? 'success' : txn.transaction_type === 'withdrawal' ? 'danger' : 'info'}>
                              {txn.transaction_type}
                            </Badge>
                          </td>
                          <td className="fw-bold">
                            ₹{parseFloat(txn.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td><small className="text-muted">{txn.payment_gateway || '—'}</small></td>
                          <td><StatusBadge status={txn.status} /></td>
                          <td><small className="text-muted">{new Date(txn.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999', backgroundColor: '#f9f9f9', borderRadius: '12px' }}>
                    <FaExchangeAlt size={36} style={{ marginBottom: '10px', opacity: 0.3 }} />
                    <p className="mb-0">No transactions found</p>
                  </div>
                )}
              </div>
            )}

            {/* ── SUPPORT TAB ── */}
            {activeTab === 'tickets' && (
              <div>
                <h6 className="fw-bold mb-3">
                  Support Tickets
                  <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#999', fontWeight: 400 }}>
                    ({statistics?.ticket_count || 0} total)
                  </span>
                </h6>
                {user.support_tickets && user.support_tickets.length > 0 ? (
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Ticket ID</th>
                        <th>Subject</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {user.support_tickets.map((ticket) => (
                        <tr key={ticket.id}>
                          <td><small className="font-monospace text-muted">{ticket.ticket_id}</small></td>
                          <td>{ticket.subject}</td>
                          <td>
                            <Badge bg={ticket.priority === 'urgent' ? 'danger' : ticket.priority === 'high' ? 'warning' : 'info'}>
                              {ticket.priority}
                            </Badge>
                          </td>
                          <td><StatusBadge status={ticket.status} /></td>
                          <td><small className="text-muted">{new Date(ticket.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999', backgroundColor: '#f9f9f9', borderRadius: '12px' }}>
                    <FaHeadset size={36} style={{ marginBottom: '10px', opacity: 0.3 }} />
                    <p className="mb-0">No support tickets found</p>
                  </div>
                )}
              </div>
            )}

          </Card.Body>
        </Card>

        {/* Update Status Modal */}
        <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '1rem' }}>
              Update Status — <strong>{user.username}</strong>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>New Status <span className="text-danger">*</span></Form.Label>
              <Form.Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="suspended">Suspended</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Remarks <span className="text-muted">(Optional)</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={statusRemarks}
                onChange={(e) => setStatusRemarks(e.target.value)}
                placeholder="Reason for status change..."
              />
            </Form.Group>
            <div style={{ padding: '10px 14px', backgroundColor: '#f1f8e9', borderRadius: '8px', fontSize: '0.8rem', color: '#558B2F' }}>
              Changing status affects user's access to platform features.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowStatusModal(false)} disabled={updating}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleStatusUpdate} disabled={updating}>
              {updating ? <><Spinner size="sm" animation="border" className="me-2" />Updating...</> : <><FaCheck className="me-2" />Update Status</>}
            </Button>
          </Modal.Footer>
        </Modal>

      </div>
    </div>
  );
};

export default AdminUserDetails;
